// Small field primitives shared by the page-level settings editors
// (ThemeEditor, SeoPanel, PageChromeEditor). They encode three rules every
// page-setting control follows:
//
//   1. UNSET is a real state. An empty field stores `undefined` so the
//      runtime default applies; the default is shown as the placeholder.
//   2. A select never misreports a saved value. A value the editor does not
//      know (hand-edited, agent-authored, from a newer schema) is listed as
//      "<value> (unrecognised)" instead of silently showing the first option.
//   3. Free text that the runtime acts on per value (a Google family name, a
//      comma list of weights) is held as a DRAFT and committed on blur/Enter,
//      so a half-typed value never reaches the theme (and never triggers a
//      webfont request for "Spa" on the way to "Space Grotesk").

export interface OptionDef {
  value: string
  label: string
}

/** '' / whitespace / NaN ⇒ undefined (runtime default); else the number. */
export function numOrUndef(raw: string): number | undefined {
  if (raw.trim() === '') return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

/** '' / whitespace ⇒ undefined; else the trimmed string. */
export function textOrUndef(raw: string): string | undefined {
  const t = raw.trim()
  return t ? t : undefined
}

/**
 * Select over a fixed option list.
 *  - `unsetLabel` adds a leading '' option meaning "not set" (onChange gets
 *    undefined). Omit it for selects that always carry a value.
 *  - A `value` that is not among `options` is appended as
 *    "<value> (unrecognised)" so it is displayed truthfully and survives
 *    until the author picks something else.
 */
export function OptionSelect({
  value, options, unsetLabel, onChange, style, disabled, title,
}: {
  value: string | undefined
  options: OptionDef[]
  unsetLabel?: string
  onChange: (v: string | undefined) => void
  style?: React.CSSProperties
  disabled?: boolean
  title?: string
}) {
  const current = value ?? ''
  const known = current === '' || options.some(o => o.value === current)
  return (
    <select
      value={current}
      disabled={disabled}
      title={title}
      onChange={e => onChange(e.target.value === '' ? undefined : e.target.value)}
      style={style}
    >
      {unsetLabel !== undefined && <option value="">{unsetLabel}</option>}
      {/* No unset option but nothing saved: keep the select honest about it. */}
      {unsetLabel === undefined && current === '' && <option value="" disabled>—</option>}
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      {!known && <option value={current}>{current} (unrecognised)</option>}
    </select>
  )
}

/**
 * Text input that edits a draft and commits on blur (or Enter). It is
 * uncontrolled and keyed by the committed value, so an outside change
 * (undo, preset switch, agent edit) remounts it with the new text while a
 * keystroke never reaches `onCommit`.
 */
export function DraftInput({
  value, onCommit, placeholder, style, title, disabled,
}: {
  value: string
  onCommit: (raw: string) => void
  placeholder?: string
  style?: React.CSSProperties
  title?: string
  disabled?: boolean
}) {
  return (
    <input
      key={value}
      type="text"
      defaultValue={value}
      placeholder={placeholder}
      title={title}
      disabled={disabled}
      onBlur={e => { if (e.target.value !== value) onCommit(e.target.value) }}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() }
        else if (e.key === 'Escape') { e.currentTarget.value = value; e.currentTarget.blur() }
      }}
      style={style}
    />
  )
}

/**
 * Parse a comma/space separated list of font weights. Keeps only integers
 * in the CSS weight range, de-duplicated and sorted; an empty result is
 * `undefined` (the runtime's default weight set).
 */
export function parseWeightList(raw: string): number[] | undefined {
  const out = raw
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(s => Number(s))
    .filter(n => Number.isInteger(n) && n >= 1 && n <= 1000)
  const uniq = [...new Set(out)].sort((a, b) => a - b)
  return uniq.length ? uniq : undefined
}
